import { useEffect, useMemo, useState } from "react";
import Button from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import { useNavigate } from "react-router-dom";
import axios, { AxiosError } from "axios";

import {
  Actions,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  ErrorText,
  FieldFull,
  Footer,
  FormGrid,
  HeaderContainer,
  InfoText,
  Input,
  Label,
  PageContainer,
  Title,
} from "./Configuracion.styles";
import { ApiError } from "../../services/apiError";
import { ConfiguracionDto } from "../../dtos/Configuracion";
import { apiService } from "../../services/apiService";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";

    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }

  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const getLoggedUserName = () => {
  try {
    const userRaw = localStorage.getItem("user");
    if (!userRaw) return "";

    const user = JSON.parse(userRaw);

    return (
      user?.userName ||
      user?.nombreUsuario ||
      user?.nombre ||
      user?.correo ||
      user?.nomina ||
      ""
    );
  } catch {
    return "";
  }
};

const ConfiguracionIndex = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exists, setExists] = useState(false);

  const [form, setForm] = useState<ConfiguracionDto>({
    baseURL: "",
    usuarioDelUltimoCambio: "",
    fechaUltimoCambio: "",
  });

  const [errors, setErrors] = useState({
    baseURL: "",
  });

  const usuarioActual = useMemo(() => getLoggedUserName(), []);

  const fetchConfiguracion = async () => {
    setLoading(true);

    try {
      const response =
        await apiService.get<ConfiguracionDto>("/api/Configuracion");

      if (response && response.baseURL) {
        setForm({
          baseURL: response.baseURL ?? "",
          usuarioDelUltimoCambio: response.usuarioDelUltimoCambio ?? "",
          fechaUltimoCambio: response.fechaUltimoCambio ?? "",
        });
        setExists(true);
      } else {
        setExists(false);
        setForm({
          baseURL: "",
          usuarioDelUltimoCambio: "",
          fechaUltimoCambio: "",
        });
        notify("No hay configuración. Use POST para crear.", "warning", 3500);
      }
    } catch (err: any) {
      const apiError = handleApiError(err);

      if (apiError.status === 404) {
        setExists(false);
        setForm({
          baseURL: "",
          usuarioDelUltimoCambio: "",
          fechaUltimoCambio: "",
        });
      } else {
        notify(apiError.message, "error", 3500);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguracion();
  }, []);

  const handleChange =
    (field: keyof ConfiguracionDto) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      setForm((prev: any) => ({
        ...prev,
        [field]: value,
      }));

      if (field === "baseURL") {
        setErrors((prev) => ({
          ...prev,
          baseURL: "",
        }));
      }
    };

  const validate = () => {
    let isValid = true;
    const nextErrors = {
      baseURL: "",
    };

    if (!form.baseURL.trim()) {
      nextErrors.baseURL = "La Base URL es obligatoria.";
      isValid = false;
    }

    setErrors(nextErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);

    const payload: ConfiguracionDto = {
      baseURL: form.baseURL.trim(),
      usuarioDelUltimoCambio: usuarioActual || "",
      fechaUltimoCambio: new Date().toISOString(),
    };

    try {
      if (exists) {
        await apiService.put("/api/Configuracion", payload);
        notify("Configuración actualizada correctamente.", "success", 3000);
      } else {
        await apiService.post("/api/Configuracion", payload);
        notify("Configuración guardada correctamente.", "success", 3000);
        setExists(true);
      }

      await fetchConfiguracion();
    } catch (err: any) {
      notify(handleApiError(err).message, "error", 3500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Title>Configuración</Title>

        <Actions>
          <Button
            icon="chevronleft"
            text="Regresar"
            stylingMode="outlined"
            type="default"
            onClick={() => navigate(-1)}
          />
        </Actions>
      </HeaderContainer>

      <Card>
        <CardHeader>
          <CardTitle>Configuración general</CardTitle>
        </CardHeader>

        <CardBody>
          <FormGrid>
            <FieldFull>
              <Label>Base URL</Label>
              <Input
                value={form.baseURL}
                onChange={handleChange("baseURL")}
                placeholder="https://midominio.com/recurso"
                disabled={loading || saving}
              />
              {errors.baseURL && <ErrorText>{errors.baseURL}</ErrorText>}
              <InfoText>
                Define la URL base que será utilizada por el sistema.
              </InfoText>
            </FieldFull>
          </FormGrid>

          <Footer>
            <Button
              text="Recargar"
              icon="refresh"
              stylingMode="outlined"
              type="default"
              onClick={fetchConfiguracion}
              disabled={loading || saving}
            />
            <Button
              text={saving ? "Guardando..." : exists ? "Actualizar" : "Guardar"}
              icon="save"
              stylingMode="contained"
              type="default"
              onClick={handleSave}
              disabled={loading || saving}
              elementAttr={{
                class: "configuracion-primary-btn",
              }}
            />
          </Footer>
        </CardBody>
      </Card>

      <style>
        {`
          .configuracion-primary-btn {
            background: #0052CC !important;
            border-color: #0052CC !important;
            color: #FFFFFF !important;
          }

          .configuracion-primary-btn .dx-button-content {
            color: #FFFFFF !important;
          }

          .configuracion-primary-btn .dx-icon {
            color: #FFFFFF !important;
          }
        `}
      </style>
    </PageContainer>
  );
};

export default ConfiguracionIndex;
